import React, { useEffect, useRef, useState } from "react";
import { Radio, Heart, Send, X, Video, Users, Globe, Trash2, ShieldOff, Upload, PlayCircle, PictureInPicture2, Share2, Link2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { ccCard, ccInput, ccBtnPink, ccBtnGhost, EmptyState } from "../care/ui";
import { LiveKitRoom, VideoConference, RoomAudioRenderer, StartAudio } from "@livekit/components-react";
import "@livekit/components-styles";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtTime = (ts) => new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const mediaSrc = (path) => (path && !path.startsWith("http") ? `${API}/api/media/${path}` : path);

export const countdown = (ts) => {
  const diff = new Date(ts) - Date.now();
  if (diff <= 0) return "starting now";
  const m = Math.floor(diff / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `in ${h}h ${m % 60}m`;
  return `in ${Math.floor(h / 24)}d`;
};

// Clipboard with fallback — never lets a rejected promise hit the dev error overlay
export const copyText = (text, msg) => {
  const done = () => toast.success(msg);
  const fallback = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      done();
    } catch {
      toast.info(text);
    }
  };
  try {
    const p = navigator.clipboard?.writeText(text);
    if (p?.then) p.then(done).catch(fallback);
    else fallback();
  } catch {
    fallback();
  }
};

// Full share payload for a live event — "HOST is LIVE on NestTrack" with description + rich preview link
export const liveShareData = (ev) => {
  const host = ev.host_name || "NestTrack";
  const ended = ev.status === "ended";
  const upcoming = ev.status === "scheduled";
  const when = ev.scheduled_at
    ? new Date(ev.scheduled_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
  return {
    title: ended ? `${ev.title} — replay on NestTrack` : upcoming ? `${ev.title} — coming up live on NestTrack` : `${host} is LIVE on NestTrack — ${ev.title}`,
    text: upcoming
      ? `${ev.description ? `${ev.description} — ` : ""}${host} goes live on NestTrack ${when}. Grab the watch link now.`
      : `${ev.description ? `${ev.description} — ` : ""}Watch ${host} live on NestTrack. Tap to join instantly, no sign-in needed.`,
    url: `${API}/api/share/live/${ev.id}`,
  };
};

export const shareLiveEvent = async (ev) => {
  const data = liveShareData(ev);
  if (navigator.share) {
    try {
      await navigator.share(data);
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
  }
  copyText(data.url, "Share link copied — it posts with a full live-event preview card.");
};

// Deep-link into the Daycare Operations live studio from any location
export const goLiveNav = () => {
  sessionStorage.setItem("nt_view", "dashboard");
  sessionStorage.setItem("nt_ops_tab", "live");
  window.location.assign("/dashboard");
};

// Per-user go-live locations — masters cut these on/off from the user profile (unset = all on)
const HOST_ROLES = ["staff", "daycare_admin", "admin"];
export const liveLocAllowed = (user, loc) => {
  if (!user) return false;
  const roles = [user.role, ...(user.extra_roles || [])];
  if (!roles.some((r) => HOST_ROLES.includes(r))) return false;
  const locs = user.live_locations;
  return !locs || locs.includes(loc);
};

export const GoLiveButton = ({ user, location, className = "", testid = "go-live-btn" }) => {
  if (!liveLocAllowed(user, location)) return null;
  return (
    <button
      onClick={goLiveNav}
      data-testid={testid}
      className={className || "inline-flex items-center gap-1.5 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs font-extrabold transition-all shadow-sm"}
    >
      <Video className="w-3.5 h-3.5" /> Go Live
    </button>
  );
};

// Shared live-event viewer — native LiveKit room while live, replay player (play/pause) after it ends
export const LiveViewer = ({ eventId, currentUser, onClose, accessKey }) => {
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const poll = useRef(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/live/${eventId}${accessKey ? `?key=${accessKey}` : ""}`, { credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not load live event");
      setEvent(body);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
    poll.current = setInterval(load, 3000);
    return () => clearInterval(poll.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const sendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      const res = await fetch(`${API}/api/live/${eventId}/comment`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: comment }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Comment failed");
      setComment("");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const heart = async () => {
    try {
      const res = await fetch(`${API}/api/live/${eventId}/heart`, { method: "POST", credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not react");
      setEvent((prev) => ({ ...prev, hearts: body.hearts, hearted: true }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const ended = event?.status === "ended";
  const pausedEv = event?.status === "paused";
  // Keyed invitees (direct join link) count as participants — full mic/camera controls, not guest lockdown
  const isGuest = !currentUser?.real && !accessKey;
  const [nativeJoin, setNativeJoin] = useState(null);
  const [nativeError, setNativeError] = useState("");
  useEffect(() => {
    if (!eventId || ended || pausedEv || event?.provider === "jitsi") return;
    fetch(`${API}/api/livekit/rooms/${eventId}/join${accessKey ? `?key=${accessKey}` : ""}`, { method: "POST", credentials: "include" })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (r.ok && d?.configured && d?.token) setNativeJoin(d);
        else setNativeError(r.status === 401 ? "Sign in to join this facility-only event." : "Video service is unavailable right now.");
      })
      .catch(() => setNativeError("Video service is unavailable right now."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, ended, pausedEv, event?.provider, accessKey]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#374151]/50 backdrop-blur-sm" onClick={onClose} data-testid="live-viewer">
      <div className="bg-white rounded-3xl border border-[#FCE6E9] shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto dark:bg-[#273244] dark:border-[#374457]" onClick={(e) => e.stopPropagation()}>
        {error ? (
          <div className="p-10 text-center" data-testid="live-error">
            <p className="text-sm font-bold text-[#B04A6B]">{error}</p>
            <button onClick={onClose} className={`${ccBtnGhost} mt-4`}>Close</button>
          </div>
        ) : !event ? (
          <p className="p-10 text-center text-sm text-[#9CA3AF] font-semibold">Loading live event…</p>
        ) : (
          <>
            <div className="relative bg-black rounded-t-3xl overflow-hidden" style={{ height: 340 }}>
              {pausedEv ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-center p-6" data-testid="live-paused-note">
                  <Radio className="w-10 h-10 text-amber-400 mb-3" />
                  <p className="text-sm font-bold text-[#9CA3AF]">The host paused this stream.</p>
                  <p className="text-[11px] text-[#6B7280] mt-1">Intermission — check back soon, it resumes right here on the same link.</p>
                </div>
              ) : ended ? (
                event.recording_path ? (
                  <video
                    src={mediaSrc(event.recording_path)}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-contain bg-black"
                    data-testid="live-replay-player"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center p-6" data-testid="live-ended-empty">
                    <PlayCircle className="w-10 h-10 text-[#4B5563] mb-3" />
                    <p className="text-sm font-bold text-[#9CA3AF]">This live event has ended.</p>
                    <p className="text-[11px] text-[#6B7280] mt-1">No replay has been uploaded yet.</p>
                  </div>
                )
              ) : event.provider === "jitsi" ? (
                <iframe
                  src={isGuest
                    ? `https://meet.jit.si/${event.room}#config.startWithAudioMuted=true&config.startWithVideoMuted=true&config.disableDeepLinking=true&config.toolbarButtons=%5B%22fullscreen%22%5D`
                    : `https://meet.jit.si/${event.room}`}
                  allow={isGuest ? "autoplay; fullscreen" : "camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"}
                  className="w-full h-full border-0"
                  data-testid="live-jitsi-frame"
                  title={event.title}
                />
              ) : nativeJoin ? (
                <div className="w-full h-full" data-testid="live-native-room">
                  <LiveKitRoom token={nativeJoin.token} serverUrl={nativeJoin.serverUrl} connect video={false} audio={false} onDisconnected={() => setNativeJoin(null)} style={{ height: "100%" }}>
                    <VideoConference />
                    <RoomAudioRenderer />
                    <StartAudio label="Enable audio" />
                  </LiveKitRoom>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center p-6" data-testid="live-native-loading">
                  <p className="text-sm font-bold text-[#9CA3AF]">{nativeError || "Connecting to the native video room…"}</p>
                </div>
              )}
              {pausedEv ? (
                <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 bg-amber-400 text-white text-[10px] font-extrabold uppercase rounded-full" data-testid="paused-badge">
                  Paused
                </span>
              ) : ended ? (
                <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 bg-[#374151] text-white text-[10px] font-extrabold uppercase rounded-full" data-testid="replay-badge">
                  <PlayCircle className="w-3 h-3" /> Replay
                </span>
              ) : (
                <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 bg-rose-500 text-white text-[10px] font-extrabold uppercase rounded-full animate-pulse" data-testid="live-badge">
                  <Radio className="w-3 h-3" /> Live
                </span>
              )}
              <button
                onClick={() => shareLiveEvent(event)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                title="Share this live event — full preview card with host, title and description"
                data-testid="live-share-btn"
              >
                <Share2 className="w-4 h-4" />
              </button>
              {!ended && event.provider !== "jitsi" && (
                <button
                  onClick={() => { window.dispatchEvent(new CustomEvent("nt-pip-live", { detail: { eventId, title: event?.title || "Live event" } })); onClose(); }}
                  data-testid="live-popout-btn"
                  title="Co-view: pop out and keep watching while you browse"
                  className="absolute top-3 right-14 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#374151] shadow"
                >
                  <PictureInPicture2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={onClose} data-testid="live-close-btn" className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#374151] shadow">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100">{event.title}</h3>
                  <p className="text-[11px] text-[#9CA3AF] font-bold mt-0.5">
                    Hosted by {event.host_name} · {event.facilityName || "NestTrack"} · {ended ? `ended ${fmtDate(event.endedAt)}` : `started ${fmtTime(event.startedAt)}`}
                    {event.is_public && <span className="ml-1.5 px-2 py-0.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[9px] font-extrabold uppercase rounded-full"><Globe className="w-2.5 h-2.5 inline -mt-0.5" /> Public</span>}
                  </p>
                  {event.description && <p className="text-xs text-[#6B7280] dark:text-slate-300 mt-2">{event.description}</p>}
                </div>
                {!ended && (
                  <div className="flex gap-2">
                    {!isGuest && (
                    <button
                      onClick={heart}
                      disabled={event.hearted}
                      data-testid="live-heart-btn"
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all shadow-sm ${event.hearted ? "bg-[#F8C8DC] text-[#B04A6B]" : "bg-[#FCE6E9] hover:bg-[#F8C8DC] text-[#374151]"}`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${event.hearted ? "fill-[#B04A6B]" : ""}`} /> {event.hearts}
                    </button>
                    )}
                    <button onClick={() => document.querySelector('[data-testid="live-native-room"]')?.scrollIntoView({ behavior: "smooth", block: "center" })} className={ccBtnPink} data-testid="live-join-btn">
                      <Video className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Join Live
                    </button>
                    <button
                      onClick={() => copyText(event.join_link || `${window.location.origin}/?live=${eventId}`, "Direct join link copied — invited viewers tap it and watch instantly.")}
                      className={ccBtnGhost}
                      title="Copy the direct join link (access key attached) for invited viewers"
                      data-testid="live-copy-join-link"
                    >
                      <Link2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Copy Join Link
                    </button>
                  </div>
                )}
              </div>

              {isGuest && !ended && (
                <p className="mt-3 px-3 py-2 bg-[#FFF2D1] border border-[#F5E3B3] rounded-xl text-[11px] font-bold text-[#8a6d1a] text-center" data-testid="live-guest-banner">
                  You're watching as a guest — sign in to comment and heart. Mic and camera controls are in the video bar.
                </p>
              )}
              {!isGuest && !currentUser?.real && !ended && (
                <p className="mt-3 px-3 py-2 bg-[#E2F4E1] border border-[#C4E5C2] rounded-xl text-[11px] font-bold text-[#2F6031] text-center" data-testid="live-invitee-banner">
                  You joined with an invite link — use the video bar to unmute your mic or turn on your camera anytime.
                </p>
              )}
              <div className="mt-5 border-t border-[#FCE6E9] dark:border-[#374457] pt-4">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2 flex items-center gap-1">
                  <Users className="w-3 h-3" /> {ended ? "Comments" : "Live comments"}
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto mb-3" data-testid="live-comments">
                  {(event.comments || []).length === 0 && <p className="text-[11px] text-[#9CA3AF] font-medium">Be the first to chime in…</p>}
                  {(event.comments || []).map((c) => (
                    <p key={c.id} className="text-xs bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl px-3 py-2 dark:bg-[#232D3D] dark:border-[#374457]" data-testid={`live-comment-${c.id}`}>
                      <span className="font-extrabold text-[#374151] dark:text-slate-100">{c.name || c.email}:</span>{" "}
                      <span className="text-[#6B7280] dark:text-slate-300">{c.text}</span>
                      <span className="text-[9px] text-[#9CA3AF] ml-1.5">{fmtTime(c.ts)}</span>
                    </p>
                  ))}
                </div>
                {!ended && (currentUser?.real ? (
                  <form onSubmit={sendComment} className="flex gap-2">
                    <input type="text" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Chime in…" className={ccInput} data-testid="live-comment-input" />
                    <button type="submit" className={`${ccBtnPink} !px-3.5`} data-testid="live-comment-send">
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  </form>
                ) : (
                  <p className="text-[11px] text-[#9CA3AF] font-bold text-center" data-testid="live-signin-hint">Sign in to comment, heart, or join the room.</p>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Host controls + active/past event lists (Daycare Ops → Live tab)
export default function LiveEvents({ ctx }) {
  const isStaffUp = ["admin", "teacher", "master"].includes(ctx.role);
  const isMaster = ctx.role === "master";
  const [events, setEvents] = useState(null);
  const [past, setPast] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", is_public: false, provider: "jitsi", scheduled_at: "" });
  const [busy, setBusy] = useState(false);
  const [watching, setWatching] = useState(null);
  const [recTarget, setRecTarget] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  const [scheduled, setScheduled] = useState(null);
  const recFileRef = useRef(null);

  const load = () => {
    fetch(`${API}/api/live/active`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setEvents)
      .catch((err) => console.debug("live list failed", err));
    fetch(`${API}/api/live/history`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setPast)
      .catch((err) => console.debug("live history failed", err));
    fetch(`${API}/api/live/upcoming`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setScheduled)
      .catch((err) => console.debug("live upcoming failed", err));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const scheduling = Boolean(form.scheduled_at);
      const res = await fetch(`${API}/api/live/${scheduling ? "schedule" : "start"}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scheduling ? { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() } : form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not start live");
      toast.success(scheduling ? "Live event scheduled — it shows as Coming Up Live with a countdown, and everyone is notified when you start." : "You're live! Facility members can watch now.");
      setForm((f) => ({ title: "", description: "", is_public: f.is_public, provider: f.provider, scheduled_at: "" }));
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const goLive = async (id) => {
    try {
      const res = await fetch(`${API}/api/live/${id}/go-live`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not go live");
      toast.success("You're live! Everyone with the link can join instantly.");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const end = async (id) => {
    try {
      const res = await fetch(`${API}/api/live/${id}/end`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not end live");
      toast.success("Live event ended. Add a replay below so families can watch it back.");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const disconnect = async (id) => {
    try {
      const res = await fetch(`${API}/api/live/${id}/disconnect`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not disconnect");
      toast.success("Live disconnected silently — no alerts or notifications were sent.");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}" and its replay permanently?`)) return;
    try {
      const res = await fetch(`${API}/api/live/${id}`, { method: "DELETE", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not delete");
      toast.success("Live event deleted.");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const pickRecording = (id) => {
    setRecTarget(id);
    recFileRef.current?.click();
  };

  const uploadRecording = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !recTarget) return;
    setRecBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch(`${API}/api/media/upload`, { method: "POST", credentials: "include", body: fd });
      const upBody = await up.json().catch(() => ({}));
      if (!up.ok) throw new Error(typeof upBody.detail === "string" ? upBody.detail : "Upload failed");
      const res = await fetch(`${API}/api/live/${recTarget}/recording`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ media_path: upBody.path }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not attach replay");
      toast.success("Replay attached — families can now watch it back with play/pause.");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRecBusy(false);
      setRecTarget(null);
    }
  };

  const canManage = (ev) => isMaster || ev.host_email === ctx.currentUserEmail || ctx.role === "admin";

  return (
    <div className="space-y-6" data-testid="live-events">
      <input ref={recFileRef} type="file" accept="video/*" className="hidden" data-testid="live-recording-input" onChange={uploadRecording} />

      {isStaffUp && (
        <form onSubmit={start} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-5 space-y-3 dark:bg-[#273244] dark:border-[#374457]" data-testid="live-start-form">
          <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2">
            <Radio className="w-4 h-4 text-rose-400" /> Go Live
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event title — e.g. Holiday Sing-Along" className={ccInput} data-testid="live-title-input" />
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" className={ccInput} data-testid="live-desc-input" />
          </div>
          <label className="flex items-center gap-2.5 flex-wrap text-xs font-bold text-[#6B7280] dark:text-slate-300" data-testid="live-schedule-label">
            <CalendarClock className="w-4 h-4 text-[#5CB3DC]" /> Schedule for later (optional)
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className={`${ccInput} !w-auto`} data-testid="live-schedule-input" />
            {form.scheduled_at && <span className="text-[10px] text-[#9CA3AF]">Shows as "Coming Up Live" with a countdown — everyone gets notified when you start.</span>}
          </label>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="live-public-label">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="w-4 h-4 accent-[#7CC98F]" data-testid="live-public-toggle" />
              <span className="text-xs font-bold text-[#2F6031]">Public — anyone can watch from the homepage (off = facility members only)</span>
            </label>
            <div className="flex items-center gap-1.5" data-testid="live-provider-toggle">
              <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide">Video via</span>
              {[["jitsi", "Jitsi (open)"], ["livekit", "LiveKit (native)"]].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForm({ ...form, provider: key })}
                  data-testid={`live-provider-${key}`}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold transition-all ${form.provider === key ? "bg-[#5CB3DC] text-white shadow-sm" : "bg-white border border-[#B3E0F2] text-[#3D7E9E] hover:bg-[#E6F2FA]"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button type="submit" disabled={busy} className={`${ccBtnPink} disabled:opacity-50`} data-testid="live-start-btn">
              {busy ? (form.scheduled_at ? "Scheduling…" : "Starting…") : form.scheduled_at ? "Schedule Live Event" : "Start Live Event"}
            </button>
          </div>
        </form>
      )}

      {events === null ? (
        <p className="text-sm text-[#9CA3AF] font-semibold">Loading live events…</p>
      ) : events.length === 0 ? (
        <EmptyState text="Nothing is live right now. Start an event above!" testId="live-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="live-list">
          {events.map((ev) => (
            <div key={ev.id} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-4 dark:bg-[#273244] dark:border-[#374457]" data-testid={`live-card-${ev.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-black text-[#374151] dark:text-slate-100 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" /> {ev.title}
                </p>
                <span className="text-[10px] font-extrabold text-[#9CA3AF] uppercase">{ev.is_public ? "Public" : "Facility"}</span>
              </div>
              <p className="text-[11px] text-[#9CA3AF] font-medium mt-1">
                {ev.host_name} · {ev.facilityName} · <Heart className="w-3 h-3 inline -mt-0.5" /> {ev.hearts}
              </p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={() => setWatching(ev.id)} className={ccBtnPink} data-testid={`live-watch-${ev.id}`}>
                  Watch
                </button>
                <button
                  onClick={() => copyText(ev.join_link || `${window.location.origin}/?live=${ev.id}`, "Direct join link copied — share it with invited viewers.")}
                  className={ccBtnGhost}
                  title="Copy the direct join link (access key attached) for invited viewers"
                  data-testid={`live-copy-link-${ev.id}`}
                >
                  <Link2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Join Link
                </button>
                <button
                  onClick={() => shareLiveEvent(ev)}
                  className={ccBtnGhost}
                  title="Share — posts with a full preview card: host name, event title, description and watch link"
                  data-testid={`live-share-${ev.id}`}
                >
                  <Share2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Share
                </button>
                {isStaffUp && (
                  <button onClick={() => end(ev.id)} className={ccBtnGhost} data-testid={`live-end-${ev.id}`}>
                    End Live
                  </button>
                )}
                {isMaster && (
                  <button
                    onClick={() => disconnect(ev.id)}
                    title="Force-end this stream with no alerts or notifications (moderation)"
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-500 rounded-full text-xs font-bold transition-colors"
                    data-testid={`live-disconnect-${ev.id}`}
                  >
                    <ShieldOff className="w-3.5 h-3.5" /> Disconnect
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Scheduled events — countdown + one-tap go-live */}
      {isStaffUp && scheduled && scheduled.length > 0 && (
        <div className={`${ccCard} p-6`} data-testid="live-scheduled">
          <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2 mb-4">
            <CalendarClock className="w-4 h-4 text-[#5CB3DC]" /> Scheduled Events
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="live-scheduled-list">
            {scheduled.map((ev) => (
              <div key={ev.id} className="bg-[#FFF9F2] border border-[#B3E0F2] rounded-2xl p-4 dark:bg-[#273244] dark:border-[#374457]" data-testid={`live-scheduled-card-${ev.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-black text-[#374151] dark:text-slate-100 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#5CB3DC]" /> {ev.title}
                  </p>
                  <span className="text-[10px] font-extrabold text-[#3D7E9E] uppercase">{countdown(ev.scheduled_at)}</span>
                </div>
                <p className="text-[11px] text-[#9CA3AF] font-medium mt-1">
                  {ev.host_name} · {new Date(ev.scheduled_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {ev.is_public ? "Public" : "Facility"}
                </p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => goLive(ev.id)} className={ccBtnPink} data-testid={`live-golive-${ev.id}`}>
                    <Radio className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Go Live Now
                  </button>
                  <button
                    onClick={() => shareLiveEvent(ev)}
                    className={ccBtnGhost}
                    title="Share the upcoming event — preview card carries the watch link"
                    data-testid={`live-share-${ev.id}`}
                  >
                    <Share2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Share
                  </button>
                  {canManage(ev) && (
                    <button
                      onClick={() => remove(ev.id, ev.title)}
                      className="flex items-center gap-1.5 px-4 py-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-500 rounded-full text-xs font-bold transition-colors"
                      data-testid={`live-delete-${ev.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past events — replays with play/pause, upload, delete */}
      <div className={`${ccCard} p-6`} data-testid="live-history">
        <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2 mb-4">
          <PlayCircle className="w-4 h-4 text-[#5CB3DC]" /> Past Events & Replays
        </h4>
        {past === null ? (
          <p className="text-xs text-[#9CA3AF] font-semibold">Loading replays…</p>
        ) : past.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] font-medium" data-testid="live-history-empty">No past events yet — ended streams appear here for replay.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="live-history-list">
            {past.map((ev) => (
              <div key={ev.id} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl overflow-hidden dark:bg-[#273244] dark:border-[#374457]" data-testid={`live-past-card-${ev.id}`}>
                {ev.recording_path ? (
                  <video
                    src={mediaSrc(ev.recording_path)}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-44 object-contain bg-black"
                    data-testid={`live-replay-${ev.id}`}
                  />
                ) : (
                  <div className="w-full h-44 bg-[#374151] flex flex-col items-center justify-center text-center p-4" data-testid={`live-no-replay-${ev.id}`}>
                    <PlayCircle className="w-8 h-8 text-[#6B7280] mb-2" />
                    <p className="text-[11px] font-bold text-[#9CA3AF]">No replay uploaded</p>
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-black text-[#374151] dark:text-slate-100">{ev.title}</p>
                    <span className="text-[10px] font-extrabold text-[#9CA3AF] uppercase">{fmtDate(ev.endedAt)}</span>
                  </div>
                  <p className="text-[11px] text-[#9CA3AF] font-medium mt-1">
                    {ev.host_name} · <Heart className="w-3 h-3 inline -mt-0.5" /> {ev.hearts}
                    {ev.disconnected_by_admin && <span className="ml-1.5 text-rose-400 font-bold">· disconnected by admin</span>}
                  </p>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button onClick={() => setWatching(ev.id)} className={ccBtnGhost} data-testid={`live-open-${ev.id}`}>
                      Open
                    </button>
                    {canManage(ev) && !ev.recording_path && (
                      <button
                        onClick={() => pickRecording(ev.id)}
                        disabled={recBusy}
                        className="flex items-center gap-1.5 px-4 py-2.5 bg-[#E6F2FA] hover:bg-[#D2E9F5] border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-xs font-bold transition-colors disabled:opacity-50"
                        data-testid={`live-upload-replay-${ev.id}`}
                      >
                        <Upload className="w-3.5 h-3.5" /> {recBusy && recTarget === ev.id ? "Uploading…" : "Upload Replay"}
                      </button>
                    )}
                    {canManage(ev) && (
                      <button
                        onClick={() => remove(ev.id, ev.title)}
                        className="flex items-center gap-1.5 px-4 py-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-500 rounded-full text-xs font-bold transition-colors"
                        data-testid={`live-delete-${ev.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {watching && <LiveViewer eventId={watching} currentUser={{ real: true, email: ctx.currentUserEmail, fullName: ctx.currentUserName }} onClose={() => { setWatching(null); load(); }} />}
    </div>
  );
}
